import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Badge, ProgressBar, Form } from 'react-bootstrap';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ArcElement, BarElement,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { FaBatteryHalf, FaSignal, FaMapMarkerAlt, FaClock, FaExclamationTriangle } from 'react-icons/fa';
import moment from 'moment';
import './SensorDetails.css';
import SiloBagDetalhado from '../SiloBagDetalhado/SiloBagDetalhado';
import ApiMServer from '../../../../services/ApiMServer';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ArcElement, BarElement, ChartDataLabels
);


const SensorDetails = ({ sensor, onClose }) => {
  const [rawReadings, setRawReadings] = useState([]);
  const [historicalData, setHistoricalData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [pontoSiloSelecionado, setPontoSiloSelecionado] = useState(null);
  const [dadosSensores, setDadosSensores] = useState([]);
  const [pontosGrafico, setPontosGrafico] = useState(25); // Estado para controlar quantidade de pontos
  const [loadingChart, setLoadingChart] = useState(false); // Estado para loading do gráfico
  const [batteryPercent, setBatteryPercent] = useState(0);
  const [signalPercent, setSignalPercent] = useState(0);
  const [voltage, setVoltage] = useState(0);
  const [rssi, setRssi] = useState(0);
  const [pressure, setPressure] = useState(null);
  const [bagSize, setBagSize] = useState(null);
  const [totalSensors, setTotalSensors] = useState(0);

  // Função para converter voltagem em porcentagem
  const voltageToPercent = (voltage) => {
    const points = [
      { v: 4.20, p: 100 },
      { v: 4.05, p: 95 },
      { v: 3.90, p: 80 },
      { v: 3.80, p: 65 },
      { v: 3.70, p: 50 },
      { v: 3.60, p: 40 },
      { v: 3.50, p: 25 },
      { v: 3.40, p: 19 },
      { v: 3.30, p: 10 },
      { v: 3.00, p: 1 },
    ];
    
    if (voltage >= 4.10) return 100;
    if (voltage < 3.00) return 0;
    
    for (let i = 0; i < points.length - 1; i++) {
      if (voltage >= points[i + 1].v && voltage <= points[i].v) {
        const v1 = points[i + 1].v;
        const v2 = points[i].v;
        const p1 = points[i + 1].p;
        const p2 = points[i].p;
        
        const percent = p1 + ((voltage - v1) / (v2 - v1)) * (p2 - p1);
        return Math.round(percent);
      }
    }
    
    return 0;
  };

  // Função para calcular porcentagem de sinal baseado no RSSI
  const rssiToPercent = (rssi) => {
    if (rssi === 0 || rssi >= -40) return 100;
    if (rssi >= -60) return 75;
    if (rssi >= -80) return 50;
    if (rssi >= -100) return 25;
    return 0;
  };

  // Função para buscar dados do gráfico com limite específico
  const fetchChartData = async (limit = 25) => {
    if (!sensor) return;
    
    setLoadingChart(true);
    try {
      const response = await ApiMServer.get(`/v2/slaves/${sensor.serial}/readings?limit=${limit}`);
      const apiData = response.data;

      if (apiData && apiData.readings && apiData.readings.length > 0) {
        setRawReadings(apiData.readings);
      }
    } catch (error) {
      console.error(`Erro ao buscar dados do gráfico para ${sensor.serial}.`, error);
    } finally {
      setLoadingChart(false);
    }
  };

  // PRIMEIRO useEffect: Busca os dados brutos da API e configura polling
  useEffect(() => {
    if (!sensor) return;

    const fetchReadings = async () => {
      try {
        const response = await ApiMServer.get(`/v2/slaves/${sensor.serial}/readings?limit=${pontosGrafico}`);
        const apiData = response.data;

        if (apiData && apiData.readings && apiData.readings.length > 0) {
          const readings = apiData.readings; // Extrai o array de readings do novo formato
          setRawReadings(readings); // Guarda a resposta completa da API
          
          // Atualiza bagSize e totalSensors da resposta
          console.log('API Response:', { bagSize: apiData.bagSize, totalSensors: apiData.totalSensors });
          if (apiData.bagSize) setBagSize(apiData.bagSize);
          if (apiData.totalSensors !== undefined) setTotalSensors(apiData.totalSensors);
          
          const latestReading = readings[0];
          
          // Calcula bateria e sinal baseado na última leitura
          const voltageValue = latestReading.voltage ?? 0;
          const rssiValue = latestReading.rssi ?? -120;
          const pressureValue = latestReading.pressure ?? null;
          
          setVoltage(voltageValue);
          setRssi(rssiValue);
          setPressure(pressureValue);
          setBatteryPercent(voltageToPercent(voltageValue));
          setSignalPercent(rssiToPercent(rssiValue));
          
          // Usa totalSensors da API para definir quantidade de pontos
          const numSensores = apiData.totalSensors || 10; // Fallback para 10 se não vier da API
          
          // Cria array de pontos baseado em totalSensors, inicialmente sem dados
          const pontosSilo = Array(numSensores).fill(null).map((_, i) => ({
            umidade: null,
            temperatura: null,
            temperatureAmbient: null,
            temperatureCO2: null,
            co2: null,
            time: `Ponto ${i + 1}`,
            hasData: false
          }));
          
          // Mapeia os sensores usando o campo index
          if (latestReading.sensors) {
            Object.values(latestReading.sensors).forEach(sensorData => {
              const sensorIndex = sensorData.index;
              if (sensorIndex && sensorIndex >= 1 && sensorIndex <= numSensores) {
                const arrayIndex = sensorIndex - 1;
                const co2Value = sensorData?.co2 ?? 0;
                const co2Percent = co2Value === -88 ? -88 : co2Value;
                
                pontosSilo[arrayIndex] = {
                  umidade: sensorData?.humidity ?? 0,
                  temperatura: sensorData?.temperature ?? 0,
                  temperatureAmbient: sensorData?.ta ?? null,
                  temperatureCO2: sensorData?.tc ?? null,
                  co2: co2Percent,
                  time: `Ponto ${sensorIndex}`,
                  hasData: true
                };
              }
            });
          }

          setDadosSensores(pontosSilo);

        } else {
            // Fallback para mock data
            const mockPontos = Array(6).fill(null).map((_, i) => ({ 
              umidade: 14.4, 
              temperatura: 25.0, 
              temperatureAmbient: 24.5, 
              temperatureCO2: 25.8, 
              co2: 344, 
              time: `Ponto ${i + 1}`,
              hasData: true
            }));
            setDadosSensores(mockPontos);
        }
      } catch (error) {
        console.error(`Erro ao buscar leituras para ${sensor.serial}.`, error);
        const mockPontos = Array(6).fill(null).map((_, i) => ({ 
          umidade: 14.4, 
          temperatura: 25.0, 
          temperatureAmbient: 24.5, 
          temperatureCO2: 25.8, 
          co2: 344, 
          time: `Ponto ${i + 1}` 
        }));
        setDadosSensores(mockPontos);
      } finally {
        setLoading(false);
      }
    };

    fetchReadings();
    // Configura polling a cada 30 segundos para atualizar automaticamente
    const intervalId = setInterval(fetchReadings, 30000);
    // Cleanup: limpa o intervalo quando o componente desmontar ou sensor mudar
    return () => clearInterval(intervalId);
  }, [sensor, pontosGrafico]);

  // useEffect para atualizar dados quando a quantidade de pontos muda
  useEffect(() => {
    if (!sensor || !pontosGrafico) return;
    
    fetchChartData(pontosGrafico);
    // Configura polling para atualizar o gráfico a cada 30 segundos
    const intervalId = setInterval(() => {
      fetchChartData(pontosGrafico);
    }, 30000);
    return () => clearInterval(intervalId);
  }, [pontosGrafico, sensor]);

  // useEffect para atualizar o ponto selecionado quando novos dados chegam
  useEffect(() => {
    if (!pontoSiloSelecionado || dadosSensores.length === 0) return;
    // Verificação de segurança
    if (!pontoSiloSelecionado.time || typeof pontoSiloSelecionado.time !== 'string') return;
    // Extrai o número do ponto (ex: "Ponto 4" -> 4)
    const pontoNumero = parseInt(pontoSiloSelecionado.time.replace('Ponto ', ''));
    if (isNaN(pontoNumero) || pontoNumero < 1 || pontoNumero > dadosSensores.length) return;
    // Atualiza o ponto selecionado com os novos dados
    const pontoAtualizado = dadosSensores[pontoNumero - 1];
    if (pontoAtualizado) {
      setPontoSiloSelecionado(pontoAtualizado);
    }
  }, [dadosSensores]); // Roda sempre que dadosSensores mudar (a cada 15 segundos)

  // SEGUNDO useEffect: Atualiza o gráfico quando o ponto selecionado muda
  useEffect(() => {
    if (!pontoSiloSelecionado || rawReadings.length === 0) {
      // Se não houver ponto selecionado ou dados brutos, limpa o gráfico
      setHistoricalData([]);
      return;
    }
    
    // Verificação de segurança para evitar erro de runtime
    if (!pontoSiloSelecionado.time || typeof pontoSiloSelecionado.time !== 'string') {
      console.warn('pontoSiloSelecionado.time não é uma string válida:', pontoSiloSelecionado);
      return;
    }
    
    // Extrai o número do ponto (ex: "Ponto 4" -> 4)
    const pontoNumero = parseInt(pontoSiloSelecionado.time.replace('Ponto ', ''));
    if (isNaN(pontoNumero)) return;

    const sensorKey = `sensor${pontoNumero}`; // Cria a chave (ex: "sensor4")
    // Mapeia os dados brutos para criar a série histórica APENAS para o sensor selecionado
    const chartData = rawReadings.map(reading => {
        const sensorData = reading.sensors?.[sensorKey];
        const co2Value = sensorData?.co2 ?? 0;
        // Trata valores -88 como inválidos (serão null para não aparecer no gráfico)
        const humidity = sensorData?.humidity === -88 ? null : (sensorData?.humidity ?? null);
        const temperatureAmbient = sensorData?.ta === -88 ? null : (sensorData?.ta ?? null);
        const temperatureCO2 = sensorData?.tc === -88 ? null : (sensorData?.tc ?? null);
        const co2 = co2Value === -88 ? null : co2Value;
        
        return {
            time: moment(reading.timestamp).add(3, 'hours').format('DD/MM HH:mm'),
            humidity: humidity,
            temperature: sensorData?.temperature === -88 ? null : (sensorData?.temperature ?? 0), // Mantém para compatibilidade
            temperatureAmbient: temperatureAmbient, // Temperatura ambiente
            temperatureCO2: temperatureCO2, // Temperatura CO2
            co2: co2, // CO2
        };
    }).reverse(); // Inverte para a ordem cronológica correta
    setHistoricalData(chartData); // Atualiza o estado do gráfico
  }, [pontoSiloSelecionado, rawReadings]); // Roda sempre que um destes dois estados mudar

  useEffect(() => {
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timeInterval);
  }, []);

  const getStatusText = (status) => {
    switch (status) {
      case 'alert': return 'ALERTA';
      case 'warning': return 'ATENÇÃO';
      default: return 'NORMAL';
    }
  };

  const getMultiLineChartData = () => {
    return {
      labels: historicalData.map(d => d.time),
      datasets: [
        { label: 'Umidade (%)', data: historicalData.map(d => (d.humidity !== null && d.humidity !== undefined && d.humidity !== -88) ? d.humidity : null), borderColor: '#17a2b8', backgroundColor: 'rgba(23, 162, 184, 0.1)', yAxisID: 'y1', tension: 0.4, fill: true, borderWidth: 2, pointBackgroundColor: '#17a2b8', pointBorderColor: '#fff', pointBorderWidth: 1, pointRadius: 3, spanGaps: true },
        { label: 'Temp. Ambiente (°C)', data: historicalData.map(d => (d.temperatureAmbient !== null && d.temperatureAmbient !== undefined && d.temperatureAmbient !== -88) ? d.temperatureAmbient : null), borderColor: '#FF4444', backgroundColor: 'rgba(255, 68, 68, 0.1)', yAxisID: 'y1', tension: 0.4, fill: true, borderWidth: 2, pointBackgroundColor: '#FF4444', pointBorderColor: '#fff', pointBorderWidth: 1, pointRadius: 3, spanGaps: true },
        { label: 'Temp. CO₂ (°C)', data: historicalData.map(d => (d.temperatureCO2 !== null && d.temperatureCO2 !== undefined && d.temperatureCO2 !== -88) ? d.temperatureCO2 : null), borderColor: '#FFD700', backgroundColor: 'rgba(255, 215, 0, 0.1)', yAxisID: 'y1', tension: 0.4, fill: true, borderWidth: 2, pointBackgroundColor: '#FFD700', pointBorderColor: '#fff', pointBorderWidth: 1, pointRadius: 3, spanGaps: true },
        { label: 'CO₂ (%)', data: historicalData.map(d => (d.co2 !== null && d.co2 !== undefined && d.co2 !== -88) ? d.co2 : null), borderColor: '#6c757d', backgroundColor: 'rgba(108, 117, 125, 0.1)', yAxisID: 'y2', tension: 0.4, fill: true, borderWidth: 2, pointBackgroundColor: '#6c757d', pointBorderColor: '#fff', pointBorderWidth: 1, pointRadius: 3, spanGaps: true },
      ],
    };
  };

  const lineChartData = getMultiLineChartData();

  const onChartClick = (event, elements) => {
    if (elements.length > 0) {
      const { index } = elements[0];
      const selectedPoint = historicalData[index];
    }
  };

  const lineChartOptions = {
    responsive: true, 
    maintainAspectRatio: false, 
    onClick: onChartClick, 
    layout: { padding: { top: 30, bottom: 10, left: 10, right: 10 } },
    animation: {
      duration: 750,
      easing: 'easeInOutQuart',
    },
    transitions: {
      active: {
        animation: {
          duration: 400
        }
      }
    },
    plugins: {
      legend: { display: true, labels: { color: '#fff' } },
      title: { 
        display: true, 
        text: `Histórico das Últimas ${pontosGrafico} Medições `, 
        color: '#fff', 
        font: { size: 16, weight: 'bold' } 
      },
      tooltip: { 
        backgroundColor: 'rgba(0, 0, 0, 0.8)', 
        titleColor: '#fff', 
        bodyColor: '#fff', 
        borderColor: 'rgba(255, 255, 255, 0.1)', 
        borderWidth: 1,
        callbacks: {
          title: function(context) {
            return `Horário: ${context[0].label}`;
          },
          label: function(context) {
            const label = context.dataset.label || '';
            const value = parseFloat(context.parsed.y).toFixed(2);
            
            // Add appropriate units based on measurement type
            if (label.includes('Umidade')) {
              return `${label}: ${value}%`;
            } else if (label.includes('Temp')) {
              return `${label}: ${value}°C`;
            } else if (label.includes('CO₂')) {
              return `${label}: ${value}%`;
            }
            return `${label}: ${value}`;
          },
          afterBody: function(context) {
            // Add summary information when multiple temperature readings are available
            const tempAmbientData = context.find(item => item.dataset.label.includes('Temp. Ambiente'));
            const tempCO2Data = context.find(item => item.dataset.label.includes('Temp. CO₂'));
            
            if (tempAmbientData && tempCO2Data) {
              const tempDiff = Math.abs(parseFloat(tempAmbientData.parsed.y) - parseFloat(tempCO2Data.parsed.y));
              return [``, `Diferença de temperatura: ${tempDiff.toFixed(1)}°C`];
            }
            return [];
          }
        },
      },
      datalabels: { display: false },
    },
    scales: {
      x: { 
        ticks: { color: '#fff', maxTicksLimit: 8 }, 
        grid: { color: 'rgba(255, 255, 255, 0.1)' },
        title: { display: true, text: 'Tempo', color: '#fff', font: { size: 11 } }
      },
      y1: { 
        type: 'linear', 
        display: true, 
        position: 'left', 
        ticks: { 
          color: '#fff',
          callback: function(value) {
            return value.toFixed(1);
          }
        }, 
        grid: { color: 'rgba(255, 255, 255, 0.1)' }, 
        title: { 
          display: true, 
          text: 'Umidade (%) / Temperaturas (°C)', 
          color: '#fff', 
          font: { size: 11 } 
        },
        suggestedMin: 0,
        suggestedMax: 100
      },
      y2: { 
        type: 'linear', 
        display: true, 
        position: 'right', 
        ticks: { 
          color: '#fff',
          callback: function(value) {
            return value.toFixed(2) + '%';
          }
        }, 
        grid: { drawOnChartArea: false }, 
        title: { 
          display: true, 
          text: 'CO₂ (%)', 
          color: '#fff', 
          font: { size: 11 } 
        },
        suggestedMin: 0
      },
    },
    interaction: { intersect: false, mode: 'index' },
    elements: { point: { hoverBackgroundColor: (context) => context.dataset.borderColor, hoverBorderColor: '#fff' } },
  };
  
  if (loading) {
      return <div className="sensor-details-container text-center"><h5>A carregar detalhes do sensor...</h5></div>;
  }

  return (
    <div className="sensor-details-container">
      <Card className="sensor-details-header bg-dark text-light">
        <Card.Body className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <div>
              <h4 className="mb-1 text-white d-flex align-items-center">
                <img src="/images/icon_silo_bolsa.png" alt="Silo Bolsa" style={{ width: '24px', height: '24px', marginRight: '8px' }} />
                {sensor.name}
              </h4>
              <div className="d-flex align-items-center gap-3">
                <span className="text-light"><FaMapMarkerAlt className="me-1" />{sensor.city}, {sensor.state}</span>
                <span className="text-light">📡 {sensor.serial}</span>
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center gap-3">
            <Badge bg={sensor.status === 'alert' ? 'danger' : sensor.status === 'warning' ? 'warning' : 'success'} className="sensor-status-badge">{getStatusText(sensor.status)}</Badge>
            <div className="text-light text-end">
              <div style={{ fontSize: '0.9rem' }}><FaClock className="me-1" />{moment(currentTime).format('DD/MM/YYYY HH:mm')}</div>
              <small className="text-muted">Última atualização: {moment(sensor.measurements.lastUpdate).add(3, 'hours').fromNow()}</small>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Row className="g-3">
        <Col lg={6}>
          <SiloBagDetalhado
            battery={batteryPercent}
            signal={signalPercent}
            voltage={voltage}
            rssi={rssi}
            onPointClick={setPontoSiloSelecionado}
            pontoSelecionado={pontoSiloSelecionado}
            dadosSiloBag={dadosSensores}
          />
        </Col>

        <Col lg={6}>
          {sensor.alerts && sensor.alerts.length > 0 && (
          <Card className="sensor-alerts-card mb-3 bg-dark text-light">
              <Card.Body>
                <div className="d-flex align-items-center mb-2"><FaExclamationTriangle className="text-warning me-2" /><h6 className="mb-0">Alertas Ativos</h6></div>
                <div className="d-flex flex-wrap gap-2">{sensor.alerts.map((alert, index) => (<Badge key={index} bg="warning" text="dark">{alert}</Badge>))}</div>
              </Card.Body>
            </Card>
          )}

          <Card className="sensor-chart-card mb-3 bg-dark text-light">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0 text-white">Gráfico Histórico</h6>
                <div className="d-flex align-items-center gap-2">
                  <Form.Label className="mb-0 text-white" style={{ fontSize: '0.9rem' }}>
                    Pontos:
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={pontosGrafico}
                    onChange={(e) => setPontosGrafico(parseInt(e.target.value))}
                    style={{ 
                      width: '80px', 
                      backgroundColor: '#495057', 
                      borderColor: '#6c757d', 
                      color: '#fff',
                      fontSize: '0.85rem'
                    }}
                    disabled={loadingChart}
                  >
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={20}>20</option>
                    <option value={25}>25</option>
                    <option value={30}>30</option>
                    <option value={35}>35</option>
                    <option value={40}>40</option>
                    <option value={45}>45</option>
                    <option value={50}>50</option>
                  </Form.Select>
                  {loadingChart && (
                    <div className="spinner-border spinner-border-sm text-light" role="status">
                      <span className="visually-hidden">Carregando...</span>
                    </div>
                  )}
                </div>
              </div>
              <div style={{ height: '300px', opacity: loadingChart ? 0.6 : 1, transition: 'opacity 0.3s ease' }}>
                <Line data={lineChartData} options={lineChartOptions} />
              </div>
            </Card.Body>
          </Card>

          <Card className="sensor-chart-card bg-dark text-light">
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="sensor-location">
                    <h6 className="mb-3">Localização do SiloBag</h6>
                    <div className="location-item">
                      <strong>Latitude:</strong> {sensor.lat ? sensor.lat.toFixed(6) : 'N/A'}
                    </div>
                    <div className="location-item">
                      <strong>Longitude:</strong> {sensor.lng ? sensor.lng.toFixed(6) : 'N/A'}
                    </div>
                    <div className="location-item">
                      <strong>Pressão:</strong> {
                        pressure !== null && pressure !== undefined
                          ? `${pressure} mbar` 
                          : 'Sem dados'
                      }
                    </div>
                    <div className="location-item">
                      <strong>Tensão:</strong> {
                        voltage > 0 
                          ? `${voltage.toFixed(2)}V` 
                          : 'Sem dados'
                      }
                    </div>
                    <div className="location-item">
                      <strong>Sinal Lora:</strong> {
                        rssi !== undefined && rssi !== null
                          ? `${rssi}dBm` 
                          : 'Sem dados'
                      }
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="sensor-summary">
                    <h6 className="mb-3">Resumo do Sensor</h6>
                    <div className="summary-item">
                      <strong>Hardware:</strong> {rawReadings.length > 0 ? rawReadings[0].hwVersion || '1.0' : '1.0'}
                    </div>
                    <div className="summary-item">
                      <strong>Firmware:</strong> {rawReadings.length > 0 ? rawReadings[0].fwVersion || '2.1' : '2.1'}
                    </div>
                    <div className="summary-item">
                      <strong>Sensores Ativos:</strong> {rawReadings.length > 0 && rawReadings[0].sensors ? Object.keys(rawReadings[0].sensors).length : 6}
                    </div>
                    <div className="summary-item">
                      <strong>Tamanho da Bolsa:</strong> {
                        bagSize !== null && bagSize !== undefined
                          ? bagSize 
                          : 'Sem dados'
                      }
                    </div>
                    <div className="summary-item">
                      <strong>Status Geral:</strong> <Badge bg={sensor.status === 'alert' ? 'danger' : sensor.status === 'warning' ? 'warning' : 'success'} className="ms-2">{getStatusText(sensor.status)}</Badge>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SensorDetails;
